/**
 * SettingsMenu — Repository-stijl dropdown rechtsboven in de header
 * die elk Settings-tab als losse modal opent. Volgt de aanpak uit het
 * impact-rapport: parallel naast de bestaande `Settings`-link gebouwd,
 * incrementeel aansluiten per onderdeel, daarna de oude `/settings`-
 * pagina opruimen.
 *
 * Stand vandaag (Stap 1):
 * - dropdown-shell met alle 13 items zichtbaar
 * - alleen Account aangesloten op een echte modal
 * - rest is grijs/disabled met "binnenkort"-tooltip — volgende slag
 *
 * Workpackages-tab blijft uitzondering en wordt nog niet als modal
 * gebouwd; opent op termijn de bestaande zware page-layout in een
 * groot popup-window.
 *
 * F10 wordt naar deze component geroute via een CustomEvent
 * (`ae:open-settings-menu`) zodat Editor.tsx z'n bestaande
 * keyboard-shortcut-useEffect daar één regel voor hoeft aan te passen.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AccountSettings } from "../settings/AccountSettings";
import { ApiTokensSettings } from "../settings/ApiTokensSettings";
import { AuditSettings } from "../settings/AuditSettings";
import { DiagramTypesSettings } from "../settings/DiagramTypesSettings";
import { MailConfigSettings } from "../settings/MailConfigSettings";
import { MaintenanceSettings } from "../settings/MaintenanceSettings";
import { PortabilitySettings } from "../settings/PortabilitySettings";
import { ProfilesSettings } from "../settings/ProfilesSettings";
import { SettingsModal } from "../settings/SettingsModal";
import { SnapshotsSettings } from "../settings/SnapshotsSettings";
import { SuperAdminSettings } from "../settings/SuperAdminSettings";
import { TeamsSettings } from "../settings/TeamsSettings";
import { TrashSettings } from "../settings/TrashSettings";
import { ValidationSettings } from "../settings/ValidationSettings";
import { WorkpackagesSettings } from "../settings/WorkpackagesSettings";
import { isAdminOfWorkspace, useSession } from "../state/session";

type SettingsTab =
  | "account"
  | "api-tokens"
  | "diagram-types"
  | "profiles"
  | "validation"
  | "portability"
  | "workpackages"
  | "teams"
  | "snapshots"
  | "trash"
  | "audit"
  | "maintenance"
  | "email"
  | "super-admin";
// NB: "quotas" zit ingebed in de Account-modal (admin-sectie onderaan),
// niet als losse dropdown-tab — daarom geen entry in deze union.

/** CustomEvent-naam die Editor.tsx (F10-shortcut) gebruikt om het menu
 *  open te klappen zonder de SettingsMenu-state te lekken. */
export const OPEN_SETTINGS_MENU_EVENT = "ae:open-settings-menu";

/** CustomEvent om direct een specifieke modal te openen vanuit een
 *  deep-link elders in de app (bv. de "Open Instellingen"-link bij een
 *  missende qa-key-foutmelding). `detail.tab` = SettingsTab. */
export const OPEN_SETTINGS_MODAL_EVENT = "ae:open-settings-modal";

/**
 * Waar de broncode van deze installatie staat (AGPL-3.0 §13). Bij het bouwen
 * te zetten met `VITE_SOURCE_URL`; ae-install doet dat.
 */
const SOURCE_URL: string =
  (import.meta.env.VITE_SOURCE_URL as string | undefined) ||
  "https://github.com/arch-designer/Arch-designer";

export function SettingsMenu() {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const workspaceId = useSession((s) => s.workspaceId);
  const isAdmin = isAdminOfWorkspace(me, workspaceId);
  const readOnly = useSession((s) => s.readOnly);

  const [open, setOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<SettingsTab | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside + ESC sluiten dropdown. WINDOW-level capture-phase
  // pointerdown: React Flow blokt pointerdown ergens tussen window-
  // en document-capture (empirisch geverifieerd 2026-05-25), dus
  // document-listeners worden niet getriggerd bij canvas-klikken.
  // Window capture is vroeger in de event-chain en wordt wel bereikt.
  // pointerdown i.p.v. mousedown omdat RF's pointerdown.preventDefault
  // per W3C-spec de gesynthetiseerde mousedown suprimeert.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  // Externe trigger (F10) — luister naar custom-event en klap open.
  useEffect(() => {
    const onOpenEvent = () => setOpen(true);
    window.addEventListener(OPEN_SETTINGS_MENU_EVENT, onOpenEvent);
    return () => window.removeEventListener(OPEN_SETTINGS_MENU_EVENT, onOpenEvent);
  }, []);

  // Externe deep-link — opent een specifieke modal direct (bv. de
  // "Open Instellingen"-link bij missende qa-key in AttributePanel).
  useEffect(() => {
    const onOpenModal = (e: Event) => {
      const detail = (e as CustomEvent<{ tab?: SettingsTab }>).detail;
      if (detail?.tab) setActiveModal(detail.tab);
    };
    window.addEventListener(OPEN_SETTINGS_MODAL_EVENT, onOpenModal);
    return () => window.removeEventListener(OPEN_SETTINGS_MODAL_EVENT, onOpenModal);
  }, []);

  /** Alle tabs zijn aangesloten. Lege Set behouden voor toekomstige
   *  features die nog niet ready zijn. */
  const PENDING_TABS: ReadonlySet<SettingsTab> = new Set<SettingsTab>();

  const openModal = (tab: SettingsTab) => {
    if (PENDING_TABS.has(tab)) return;
    setActiveModal(tab);
    setOpen(false);
  };

  const closeModal = () => setActiveModal(null);

  // Trash is sinds 2026-05-21 member-toegankelijk: non-admins zien
  // alleen eigen weggooi-items (server-side filter), admins zien alles.
  // Daarom verhuist 'ie naar de algemene groep.
  const allGeneralItems: SettingsTab[] = [
    "account",
    "api-tokens",
    "diagram-types",
    "profiles",
    "validation",
    "trash",
  ];
  // Met alleen leesrecht op het actieve workpackage blijven je eigen account
  // en je API-tokens bereikbaar; de rest wijzigt het model of z'n inrichting,
  // en dat doet een lezer niet (leesrecht is kijken, 2026-10-17).
  const generalItems: SettingsTab[] =
    readOnly && !isAdmin ? ["account", "api-tokens"] : allGeneralItems;
  const adminItems: SettingsTab[] = [
    "workpackages",
    "teams",
    "snapshots",
    "portability",
    "audit",
    "maintenance",
    "email",
  ];

  return (
    <>
      <div className="relative" ref={ref}>
        <button
          type="button"
          className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 font-semibold text-(--ld-text) hover:bg-(--ld-raised)"
          onClick={() => setOpen((v) => !v)}
          title={t("app.header.settingsMenuTitle")}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {t("app.header.settingsMenu")} ▾
        </button>
        {open && (
          <div className="ledger absolute right-0 z-50 mt-1 max-h-[calc(100vh-4rem)] w-64 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-(--ld-control) bg-(--ld-surface) p-1.5 shadow-lg">
            <MenuGroupLabel first>{t("app.header.settingsMenuGeneralGroup")}</MenuGroupLabel>
            {generalItems.map((tab) => (
              <MenuItem
                key={tab}
                label={t(`settings.tabs.${tabI18nKey(tab)}`)}
                disabled={PENDING_TABS.has(tab)}
                onClick={() => openModal(tab)}
              />
            ))}
            {isAdmin && (
              <>
                <MenuGroupLabel>{t("app.header.settingsMenuAdminGroup")}</MenuGroupLabel>
                {adminItems.map((tab) => (
                  <MenuItem
                    key={tab}
                    label={t(`settings.tabs.${tabI18nKey(tab)}`)}
                    disabled={PENDING_TABS.has(tab)}
                    onClick={() => openModal(tab)}
                  />
                ))}
              </>
            )}
            {me?.is_super_admin && (
              <>
                <MenuGroupLabel>{t("app.header.settingsMenuInstallGroup")}</MenuGroupLabel>
                <MenuItem
                  label={t("settings.tabs.superAdmin")}
                  onClick={() => openModal("super-admin")}
                />
              </>
            )}
            {/* AGPL-3.0 §13: wie de app gebruikt, kan de broncode krijgen. */}
            <div className="mx-1.5 my-1.5 border-(--ld-rule) border-t" />
            <a
              href={SOURCE_URL}
              target="_blank"
              rel="noreferrer"
              className="block rounded-md px-2.5 py-1.5 text-(--ld-muted) text-xs hover:bg-(--ld-raised) hover:text-(--ld-text)"
            >
              {t("app.header.sourceCode")}
            </a>
          </div>
        )}
      </div>

      {activeModal === "account" && (
        <SettingsModal title={t("settings.tabs.account")} onClose={closeModal} tone="ledger">
          <AccountSettings />
        </SettingsModal>
      )}
      {activeModal === "api-tokens" && (
        <SettingsModal
          title={t("settings.tabs.apiTokens")}
          onClose={closeModal}
          maxWidth="max-w-4xl"
          tone="ledger"
        >
          <ApiTokensSettings />
        </SettingsModal>
      )}
      {activeModal === "diagram-types" && (
        <SettingsModal
          title={t("settings.tabs.diagramTypes")}
          onClose={closeModal}
          maxWidth="max-w-5xl"
          tone="ledger"
        >
          <DiagramTypesSettings />
        </SettingsModal>
      )}
      {activeModal === "profiles" && (
        <SettingsModal
          title={t("settings.tabs.profiles")}
          onClose={closeModal}
          maxWidth="max-w-4xl"
          tone="ledger"
        >
          <ProfilesSettings />
        </SettingsModal>
      )}
      {activeModal === "validation" && (
        <SettingsModal
          title={t("settings.tabs.validation")}
          onClose={closeModal}
          maxWidth="max-w-4xl"
          tone="ledger"
        >
          <ValidationSettings onClose={closeModal} />
        </SettingsModal>
      )}
      {activeModal === "trash" && workspaceId && (
        <SettingsModal
          title={t("settings.tabs.trash")}
          onClose={closeModal}
          maxWidth="max-w-4xl"
          tone="ledger"
        >
          <TrashSettings workspaceId={workspaceId} />
        </SettingsModal>
      )}
      {activeModal === "audit" && isAdmin && workspaceId && (
        <SettingsModal
          title={t("settings.tabs.audit")}
          onClose={closeModal}
          maxWidth="max-w-5xl"
          tone="ledger"
        >
          <AuditSettings workspaceId={workspaceId} />
        </SettingsModal>
      )}
      {activeModal === "portability" && isAdmin && (
        <SettingsModal
          title={t("settings.tabs.portability")}
          onClose={closeModal}
          maxWidth="max-w-4xl"
          tone="ledger"
        >
          <PortabilitySettings />
        </SettingsModal>
      )}
      {activeModal === "snapshots" && isAdmin && workspaceId && (
        <SettingsModal
          title={t("settings.tabs.snapshots")}
          onClose={closeModal}
          maxWidth="max-w-5xl"
          tone="ledger"
        >
          <SnapshotsSettings workspaceId={workspaceId} />
        </SettingsModal>
      )}
      {activeModal === "email" && isAdmin && workspaceId && (
        <SettingsModal title={t("settings.tabs.email")} onClose={closeModal} tone="ledger">
          <MailConfigSettings workspaceId={workspaceId} />
        </SettingsModal>
      )}
      {activeModal === "teams" && isAdmin && workspaceId && (
        <SettingsModal
          title={t("settings.tabs.teams")}
          onClose={closeModal}
          maxWidth="max-w-4xl"
          tone="ledger"
        >
          <TeamsSettings workspaceId={workspaceId} />
        </SettingsModal>
      )}
      {activeModal === "maintenance" && isAdmin && workspaceId && (
        <SettingsModal title={t("settings.tabs.maintenance")} onClose={closeModal} tone="ledger">
          <MaintenanceSettings workspaceId={workspaceId} />
        </SettingsModal>
      )}
      {/* Workpackages — breedste popup-window (max-w-7xl). De geneste
          folder-permissie-tabel en sub-BranchesSettings-blok hebben
          horizontaal ruimte nodig die de andere modals niet krijgen. */}
      {activeModal === "workpackages" && isAdmin && workspaceId && (
        <SettingsModal
          title={t("settings.tabs.workpackages")}
          onClose={closeModal}
          maxWidth="max-w-7xl"
          tone="ledger"
        >
          <WorkpackagesSettings workspaceId={workspaceId} />
        </SettingsModal>
      )}
      {activeModal === "super-admin" && me?.is_super_admin && (
        <SettingsModal
          title={t("settings.tabs.superAdmin")}
          onClose={closeModal}
          maxWidth="max-w-5xl"
          tone="ledger"
          flush
        >
          <SuperAdminSettings onClose={closeModal} />
        </SettingsModal>
      )}
    </>
  );
}

/** Mapping van interne tab-id naar i18n-key. Sommige keys gebruiken
 *  camelCase (diagramTypes), de tab-id zelf kebab-case. */
function tabI18nKey(tab: SettingsTab): string {
  switch (tab) {
    case "diagram-types":
      return "diagramTypes";
    case "super-admin":
      return "superAdmin";
    case "api-tokens":
      return "apiTokens";
    default:
      return tab;
  }
}

function MenuItem({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? t("app.header.settingsMenuPending") : undefined}
      className={
        // Zelfde maat als de titels in het Repository-menu.
        disabled
          ? "ml-6 block w-[calc(100%-1.5rem)] cursor-not-allowed rounded-md px-2.5 py-1.5 text-left text-(--ld-disabled-ink) text-[13px]"
          : "ml-6 block w-[calc(100%-1.5rem)] rounded-md px-2.5 py-1.5 text-left text-(--ld-text) text-[13px] transition-colors hover:bg-(--ld-raised)"
      }
    >
      {label}
    </button>
  );
}

/** Kop boven een groep in het Instellingen-menu: vet, met een lijn eronder; de
 *  items staan ingesprongen eronder. */
function MenuGroupLabel({ children, first }: { children: React.ReactNode; first?: boolean }) {
  return (
    <div
      className={
        first
          ? "mx-1.5 mt-1 mb-1 border-(--ld-border) border-b px-1 pb-1.5 font-semibold text-(--ld-text) text-[13px]"
          : "mx-1.5 mt-3.5 mb-1 border-(--ld-border) border-b px-1 pb-1.5 font-semibold text-(--ld-text) text-[13px]"
      }
    >
      {children}
    </div>
  );
}
